import { useEffect, useRef, useState, useCallback } from 'react';
import './Testimonials.css';

const TESTIMONIALS = [
  {
    name: 'Priya & Karthik',
    event: 'Wedding — Madurai',
    stars: 5,
    text: 'Brand Partner Studio made our wedding day feel like a Bollywood film. Every candid shot was pure magic. We still get emotional looking at the album — true artists who feel the moment before they frame it.',
    initials: 'PK',
  },
  {
    name: 'Anitha Rajan',
    event: 'Maternity Shoot — Chennai',
    stars: 5,
    text: 'I was nervous about a maternity shoot but the team made me feel like a goddess. The lighting, the props, the music in the background — it was a full experience. The photos are breathtaking.',
    initials: 'AR',
  },
  {
    name: 'Deepak & Meena',
    event: 'Pre-Wedding — Kodaikanal',
    stars: 5,
    text: 'Our pre-wedding shoot in Kodaikanal was everything we dreamed of. They scouted the spots, planned the golden hour perfectly, and handled everything. The cinematic reels still make our hearts skip.',
    initials: 'DM',
  },
  {
    name: 'Lakshmi Suresh',
    event: 'Baby Shoot — Coimbatore',
    stars: 5,
    text: 'The team was SO patient with my 6-month-old. The baby studio setup was beautiful — soft lighting, gentle music, and the sweetest props. Every photo looks like a painting. Highly recommend!',
    initials: 'LS',
  },
  {
    name: 'Ramesh Narayanan',
    event: 'Corporate Event — Trichy',
    stars: 5,
    text: 'Hired them for a large corporate event with 400+ guests. Professional, punctual, and the edited photos were delivered within 48 hours. Exceptional quality and service. Will book again without hesitation.',
    initials: 'RN',
  },
];

function Paperclip() {
  return (
    <svg viewBox="0 0 28 68" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14 4 C6 4 2 10 2 17 L2 52 C2 60 8 66 16 66 C24 66 26 60 26 52 L26 18 C26 12 22 8 16 8 C10 8 8 12 8 18 L8 50 C8 54 10 58 14 58 C18 58 20 54 20 50 L20 20"
        stroke="#c8001a"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/* Animated quote — word by word reveal */
function AnimatedQuote({ text, cardKey }) {
  const words = text.split(' ');
  const [shownCount, setShownCount] = useState(0);
  const [cursorVisible, setCursorVisible] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    setShownCount(0);
    setCursorVisible(true);
    clearInterval(timerRef.current);

    let i = 0;
    // Small delay before writing starts
    const startDelay = setTimeout(() => {
      timerRef.current = setInterval(() => {
        i++;
        setShownCount(i);
        if (i >= words.length) {
          clearInterval(timerRef.current);
          // Cursor blinks a bit then fades
          setTimeout(() => setCursorVisible(false), 2200);
        }
      }, 55); // speed per word
    }, 300);

    return () => {
      clearTimeout(startDelay);
      clearInterval(timerRef.current);
    };
  }, [cardKey]);

  return (
    <p className="testi-quote">
      {words.map((word, i) => (
        <span
          key={i}
          className="testi-word"
          style={{
            opacity: i < shownCount ? 1 : 0,
            transform: i < shownCount ? 'translateY(0)' : 'translateY(8px)',
            transition: 'opacity 0.25s ease, transform 0.25s ease',
            marginRight: '0.28em',
          }}
        >
          {word}
        </span>
      ))}
      <span className={`testi-cursor ${cursorVisible ? 'visible' : ''}`} />
    </p>
  );
}

export default function Testimonials() {
  const sectionRef            = useRef(null);
  const [visible, setVisible] = useState(false);
  const [active, setActive]   = useState(0);
  const [cardKey, setCardKey] = useState(0);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setActive(a => (a + 1) % TESTIMONIALS.length);
      setCardKey(k => k + 1);
    }, 8000);
    return () => clearInterval(t);
  }, []);

  const goTo = useCallback((i) => {
    setActive(i);
    setCardKey(k => k + 1);
  }, []);

  const prev = () => goTo((active - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  const next = () => goTo((active + 1) % TESTIMONIALS.length);

  const t = TESTIMONIALS[active];

  return (
    <section
      id="testimonials"
      className={`testi-section ${visible ? 'testi-visible' : ''}`}
      ref={sectionRef}
    >
      <div className="testi-bg-quote">"</div>

      {/* Header */}
      <div className="testi-header">
        <div className="testi-eyebrow">What Our Clients Say</div>
        <h2 className="testi-heading">
          Client <span className="testi-italic">Testimonials</span>
        </h2>
      </div>

      {/* Paper Stage */}
      <div className="testi-stage">
        {/* Back shadow cards */}
        <div className="testi-stack-card" />
        <div className="testi-stack-card" />

        {/* Main paper card */}
        <div className="testi-card" key={cardKey}>

          <div className="testi-clip"><Paperclip /></div>

          {/* Stars */}
          <div className="testi-stars">
            {Array.from({ length: t.stars }).map((_, i) => (
              <span key={i} className="testi-star">★</span>
            ))}
          </div>

          {/* Writing animation */}
          <AnimatedQuote text={t.text} cardKey={cardKey} />

          {/* Author */}
          <div className="testi-author-row">
            <div className="testi-signature" style={{ animationDelay: `${t.text.split(' ').length * 55 + 400}ms` }}>
              <span className="testi-sig-label">— Review by</span>
              <span className="testi-name"
                style={{ animationDelay: `${t.text.split(' ').length * 55 + 300}ms` }}>
                {t.name}
              </span>
              <span className="testi-event">{t.event}</span>
            </div>
            <div className="testi-avatar">{t.initials}</div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="testi-controls">
        <button className="testi-arrow" onClick={prev} aria-label="Previous">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M19 12H5M11 6l-6 6 6 6" />
          </svg>
        </button>
        <div className="testi-dots">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              className={`testi-dot ${i === active ? 'active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Testimonial ${i + 1}`}
            />
          ))}
        </div>
        <button className="testi-arrow" onClick={next} aria-label="Next">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </button>
      </div>

    </section>
  );
}